import React from "react";
import {
  FIRST_TIME_CLIENT,
  INewAppointmentArgs,
  IPathState,
  IAppointmentRouteState,
  RESCHEDULE_LIST,
  SETUP_PAYMENT,
} from "./index";
import { Alert, Button, Divider, Space } from "antd";
import OpeningLoading from "../OpeningLoading";
import { BOOKING_BLOCKED_MESSAGE } from "@/lib/api/openingsLocations";
import { extractErrorMessage } from "@/lib/helpers";
import type { AppointmentForm } from "@/lib/hooks/useAppointment";
import { useBookingApi } from "./bookingApi";

const VERIFICATION_RESEND_COOLDOWN_SECONDS = 45;

interface IProps {
  appointment: AppointmentForm;
  routeState: IAppointmentRouteState;
  onRouteChange: (path: IPathState, state: any) => void;
  onCreate: (args: INewAppointmentArgs) => void;
}
const RescheduleMain = ({
  appointment,
  routeState,
  onRouteChange,
  onCreate,
}: IProps) => {
  const bookingApi = useBookingApi();
  const [isSubmitting, setSubmitting] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const { upcomingAppointments, phoneNumber, client, paymentMethodId } =
    routeState;
  const customerId = client?.customerId;
  // Re-resolve the booking block here as a safety net. A blocked customer with
  // upcoming appointments can reach this scene if the phone-lookup gate did not
  // surface the block; we must not let them start a new booking or reschedule.
  const [isBlocked, setIsBlocked] = React.useState(false);
  const [isCheckingBlock, setCheckingBlock] = React.useState(
    Boolean(customerId),
  );
  React.useEffect(() => {
    let isMounted = true;
    if (!customerId) {
      setCheckingBlock(false);
      return;
    }
    setCheckingBlock(true);
    bookingApi
      .isCustomerBookingBlocked(customerId, appointment)
      .then((blocked) => {
        if (isMounted) setIsBlocked(blocked);
      })
      .catch(() => {
        // On failure, fall through to server-side enforcement on create.
      })
      .finally(() => {
        if (isMounted) setCheckingBlock(false);
      });
    return () => {
      isMounted = false;
    };
  }, [customerId, appointment, bookingApi]);

  // Reschedule must always be gated behind OTP verification before any
  // appointment details are revealed. On Openings-backed locations the list is
  // hidden until verification (length === 0); on legacy locations the lookup
  // returns the list inline, so we still require verification here before
  // showing it. Once verified (verificationCode present) we skip re-sending.
  const hasListToReveal =
    Boolean(routeState.hasUpcomingAppointments) ||
    upcomingAppointments.length > 0;
  const listAlreadyLoaded = upcomingAppointments.length > 0;
  const needsVerificationForList =
    hasListToReveal && !routeState.verificationCode;
  const handleNewAppointment = () => {
    if (isBlocked) {
      setActionError(BOOKING_BLOCKED_MESSAGE);
      return;
    }
    setActionError(null);
    // When the selected services require a deposit/payment method and we have
    // not collected one yet, route through the payment step first. Otherwise
    // the API rejects the booking with "A saved payment method is required".
    if (routeState.isCreditCardRequired && !paymentMethodId) {
      onRouteChange(SETUP_PAYMENT, routeState);
      return;
    }

    setSubmitting(true);
    onCreate({
      firstName: client ? client.firstName : "Unknown",
      lastName: client ? client.lastName : "Unknown",
      phoneNumber,
      paymentMethodId,
      errorCallback: (errorMessage) => {
        if (errorMessage) setActionError(errorMessage);
        setSubmitting(false);
      },
    });
  };

  const handleReschedule = async () => {
    if (isBlocked) {
      setActionError(BOOKING_BLOCKED_MESSAGE);
      return;
    }
    setActionError(null);
    if (needsVerificationForList) {
      setSubmitting(true);
      try {
        await bookingApi.sendVerificationCode(
          phoneNumber,
          appointment,
          routeState.verificationPurpose,
        );
        onRouteChange(FIRST_TIME_CLIENT, {
          ...routeState,
          verifyForUpcomingAppointments: true,
          returnToRescheduleList: true,
          listAlreadyLoaded,
          verificationResendAvailableAt:
            Date.now() + VERIFICATION_RESEND_COOLDOWN_SECONDS * 1000,
        });
      } catch (error) {
        setActionError(extractErrorMessage(error));
        setSubmitting(false);
      }
      return;
    }

    onRouteChange(RESCHEDULE_LIST, routeState);
  };
  // if (isSubmitting) {
  //   return (
  //     <div className="has-text-centered">
  //       <OpeningLoading />
  //       <br />
  //       <br />
  //       {needsVerificationForList
  //         ? "Sending your verification code. Please hold on a moment..."
  //         : "Your appointment is being submitted. Please hold on a moment..."}
  //     </div>
  //   );
  // }
  return (
    <>
      {isCheckingBlock ? (
        <div className="has-text-centered">
          <OpeningLoading />
        </div>
      ) : isBlocked ? (
        <Alert type="error" showIcon message={BOOKING_BLOCKED_MESSAGE} />
      ) : (
        <>
          {needsVerificationForList
            ? "Looks like you already have an upcoming appointment. Would you like to schedule a new one, or reschedule an existing one?"
            : `Looks like you already have ${upcomingAppointments.length} upcoming appointment${upcomingAppointments.length > 1 ? "s" : ""}! Would you like to schedule a new one, or perhaps reschedule an existing one?`}
          <Divider dashed />
          {actionError && (
            <Alert
              type="error"
              showIcon
              message={actionError}
              style={{ marginBottom: 16 }}
            />
          )}
          <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
            <Button
              block
              type="primary"
              disabled={isSubmitting}
              onClick={handleNewAppointment}
            >
              Make another appointment
            </Button>
            <Button
              block
              color="default"
              variant="filled"
              disabled={isSubmitting}
              onClick={handleReschedule}
            >
              Reschedule
            </Button>
          </Space>
        </>
      )}
    </>
  );
};

export default RescheduleMain;
